import { Box, Stack, alpha } from "@mui/material";
import { Icon, Typography } from "@platform/design-system";

import { RUN_STATE_TREATMENTS } from "../../runStateTreatment";
import { RUN_STATE_LABELS, type RunStateKey } from "../../runStates";

import type { RunStateCard } from "../../types";

/**
 * Awaiting Input is the only card the design tints. It is the one that says
 * the RFP cannot be submitted, and the tint is what makes that readable
 * without counting.
 */
const isBlocking = (state: RunStateKey): boolean => state === "awaiting-input";

const StateCard = ({ card }: { card: RunStateCard }): React.JSX.Element => {
  const { icon, iconColor } = RUN_STATE_TREATMENTS[card.state];

  return (
    <Box
      sx={{
        flex: 1,
        p: 2,
        ...(isBlocking(card.state) && {
          // No design-system surface carries a status tint yet.
          backgroundColor: (theme) => alpha(theme.palette.error.main, 0.06),
        }),
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Icon name={icon} size="sm" color={iconColor} />
          <Typography variant="subtitle2">
            {RUN_STATE_LABELS[card.state]}
          </Typography>
        </Stack>
        <Typography variant="header-medium">{card.headline}</Typography>
        <Typography variant="caption" color="textSecondary">
          {card.caption}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {card.breakdown.join(" • ")}
        </Typography>
      </Stack>
    </Box>
  );
};

interface Props {
  cards: readonly RunStateCard[];
}

/** The run's three state counts, side by side. */
const RunStateCards = ({ cards }: Props): React.JSX.Element => (
  <Stack
    direction={{ xs: "column", md: "row" }}
    divider={<Box sx={{ borderLeft: 1, borderColor: "divider" }} />}
    sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}
  >
    {cards.map((card) => (
      <StateCard key={card.state} card={card} />
    ))}
  </Stack>
);

export { RunStateCards };
export type { Props as RunStateCardsProps };
